/* eslint-disable ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/require-tagless-components */
// Extends from
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
// Ember imports
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { setDiff } from '@ember/object/computed';
// External imports
import config from 'linced-admin/config/environment';

export default class ApplyTemplate extends Component {
  @action
  setCurrentStep(value) {
    this.currentStep = value;
  }

  @action
  setSelectedGroupFilter(value) {
    this.selectedGroupFilter = value;
  }

  // Services
  @service
  fetch;

  // Attributes
  // Passed in
  customMessage = '';

  schoolGroups = [];
  schools = [];
  serviceName = '';
  sortByAttr = '';
  templateEndpoint = '';
  templates = [];
  templateType = '';
  usageSets = [];

  // Dialogue attributes
  @tracked
  applyConfirmed = false;

  @tracked
  applyDialogOpen = false;
  @tracked
  applyErrors = null;
  @tracked
  applyInProgress = false;
  @tracked
  currentStep = 0;
  @tracked selectedGroupFilter = null;
  @tracked
  selectedSchoolsToApply = [];
  @tracked
  selectedTemplatesToApply = [];
  @tracked
  selectedUsageSetsFilter = [];

  // Computed
  @setDiff('usageSets', 'selectedUsageSetsFilter')
  remainingUsageSetsFilter;

  @computed('schools.[]', 'selectedGroupFilter')
  get filteredSchools() {
    let schools = this.schools;

    if (!isEmpty(this.selectedGroupFilter)) {
      schools = this.selectedGroupFilter.get('schools');
    }

    return schools;
  }

  @computed('templates.[]', 'selectedUsageSetsFilter.[]')
  get filteredTemplates() {
    return this.templates.filter((template) => {
      if (!isEmpty(this.selectedUsageSetsFilter)) {
        return this.selectedUsageSetsFilter.some((usageSet) =>
          template.get('usageSetsArray').includes(usageSet),
        );
      }
      return true;
    });
  }

  @action
  openApplyDialog() {
    this.applyDialogOpen = true;
    this.currentStep = 0;
    this.selectedSchoolsToApply = [];
    this.selectedTemplatesToApply = [];
    this.selectedUsageSetsFilter = [];
  }

  @action
  closeApplyDialog() {
    this.applyConfirmed = false;
    this.applyDialogOpen = false;
    this.applyErrors = null;
    this.selectedSchoolsToApply = [];
    this.selectedTemplatesToApply = [];
    this.selectedUsageSetsFilter = [];
  }

  @action
  toggleApplyTemplate(tagCategory, checked) {
    if (checked) {
      this.selectedTemplatesToApply = [
        ...this.selectedTemplatesToApply,
        tagCategory,
      ];
    } else {
      this.selectedTemplatesToApply = this.selectedTemplatesToApply.filter(
        (t) => t !== tagCategory,
      );
    }
  }

  @action
  addUsageSetFilter(usageSet) {
    this.selectedUsageSetsFilter = [...this.selectedUsageSetsFilter, usageSet];
  }

  @action
  removeUsageSetFilter(usageSet) {
    this.selectedUsageSetsFilter = this.selectedUsageSetsFilter.filter(
      (u) => u !== usageSet,
    );
  }

  @action
  toggleApplySchool(school, checked) {
    if (checked) {
      this.selectedSchoolsToApply = [...this.selectedSchoolsToApply, school];
    } else {
      this.selectedSchoolsToApply = this.selectedSchoolsToApply.filter(
        (s) => s !== school,
      );
    }
  }

  @action
  toggleAllSchools(schools, checked) {
    if (checked) {
      this.selectedSchoolsToApply = [...schools];
    } else {
      this.selectedSchoolsToApply = [];
    }
  }

  @action
  toggleAllTemplates(templates, checked) {
    if (checked) {
      this.selectedTemplatesToApply = [...templates];
    } else {
      this.selectedTemplatesToApply = [];
    }
  }

  @action
  applySelected() {
    this.applyConfirmed = true;
    this.applyInProgress = true;
    let errors = {};
    let chain = this.selectedTemplatesToApply.reduce(
      (templatePromise, template) => {
        return templatePromise.then(() => {
          let applyUri = `${config.APP.apiHost}/${this.serviceName}/${config.APP.apiPath}/templates/${this.templateEndpoint}/${template.id}/:apply`;
          return this.selectedSchoolsToApply.reduce((schoolPromise, school) => {
            let options = {
              data: JSON.stringify({ schoolId: school.id }),
            };
            return schoolPromise.then(() => {
              return this.fetch.post(applyUri, options).catch((error) => {
                // Store all occurred errors per school
                if (!errors[school.name]) {
                  errors[school.name] = [];
                }
                errors[school.name].push(
                  `Error applying ${template.label} - ${error.message}`,
                );
              });
            });
          }, Promise.resolve());
        });
      },
      Promise.resolve(),
    );

    return chain.then(() => {
      this.applyInProgress = false;
      // A normal !isEmpty won't work with an object
      if (Object.keys(errors).length > 0) {
        this.applyErrors = errors;
      }
    });
  }
}
